import { useState, useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, Smartphone, BatteryCharging, Camera, Wrench, Droplets, Truck, AlertTriangle, UserCheck, BadgePercent, ArrowLeft, ShieldCheck, Layers } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const SHIPPING_FEE = 6.95;

const inputCls =
  "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

const empty = { first_name: "", last_name: "", email: "", phone: "", street: "", zip: "", city: "", message: "", device_code: "" };

// Pick a reasonable icon per service key; falls back to a wrench for anything new.
const ICONS = { akku: BatteryCharging, kamera: Camera, ladebuchse: Droplets, wasserschaden: Droplets };
const iconFor = (key) => ICONS[key] || Wrench;

export default function Order() {
  const { state } = useLocation();
  const preselect = state?.preselect || "";
  const [pricing, setPricing] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [device, setDevice] = useState("");
  const [wantDisplay, setWantDisplay] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [displayType, setDisplayType] = useState("oled");
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [taxNote, setTaxNote] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [matchedCustomer, setMatchedCustomer] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [addProtection, setAddProtection] = useState(false);
  const [deviceSerial, setDeviceSerial] = useState("");
  const [protectionSettings, setProtectionSettings] = useState(null);
  const [wantPanzerglas, setWantPanzerglas] = useState(false);
  const [wantCleaning, setWantCleaning] = useState(false);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/protection/settings").then((r) => setProtectionSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/pricing").then((r) => {
      setPricing(r.data);
      if (r.data[0]) setDevice((d) => d || r.data[0].model);
    }).catch(() => {});
    api.get("/service-types").then((r) => setServiceTypes(r.data.filter((s) => s.active))).catch(() => {});
    api.get("/company").then((r) => {
      const c = r.data || {};
      setCompany(c);
      setTaxNote(c.kleinunternehmer
        ? "Preis inkl. Versand. Kleinunternehmer gemäß § 19 UStG – keine Umsatzsteuer."
        : `Preis inkl. Versand und ${c.tax_rate || 19}% MwSt.`);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (preselect === "Akkutausch") { setSelectedKeys(["akku"]); setWantDisplay(false); }
    if (preselect === "Display-Reparatur") { setWantDisplay(true); }
  }, [preselect]);

  const row = useMemo(() => pricing.find((p) => p.model === device), [pricing, device]);
  const oledAvailable = row ? row.oled_available !== false : true;

  // force LCD if OLED not available
  useEffect(() => {
    if (row && !oledAvailable && displayType === "oled") setDisplayType("lcd");
  }, [row, oledAvailable, displayType]);

  const priceOf = (key) => row?.service_prices?.[key]?.sell_price || 0;
  const originalOf = (key) => row?.service_prices?.[key]?.original_price || 0;
  const displayPriceOled = row?.oled_price || 0;
  const displayPriceLcd = row?.lcd_price || 0;
  const oledOriginal = row?.oled_original_price || 0;
  const lcdOriginal = row?.lcd_original_price || 0;
  const displayPrice = row ? (displayType === "oled" && oledAvailable ? displayPriceOled : displayPriceLcd) : 0;
  const servicesTotal = selectedKeys.reduce((sum, k) => sum + priceOf(k), 0);
  const base = (wantDisplay ? displayPrice : 0) + servicesTotal;
  const hasService = wantDisplay || selectedKeys.length > 0;
  const discountPercent = matchedCustomer?.discount_percent || 0;
  const loyaltyAmount = discountPercent > 0 ? base * (discountPercent / 100) : 0;
  const couponPercentSum = coupons.filter((c) => c.type === "percent").reduce((s, c) => s + c.value, 0);
  const couponFixedSum = coupons.filter((c) => c.type === "fixed").reduce((s, c) => s + c.value, 0);
  const couponAmount = Math.min(base * (couponPercentSum / 100) + couponFixedSum, base);
  const freeShipping = coupons.some((c) => c.type === "free_shipping");
  // Codes stack additively with each other. The combined stack only competes against the
  // loyalty-% discount (whichever saves more wins) — matches backend logic.
  const couponWins = coupons.some((c) => c.type !== "free_shipping") && couponAmount >= loyaltyAmount;
  const discountAmount = couponWins ? couponAmount : loyaltyAmount;
  const discountedBase = Math.max(base - discountAmount, 0);
  const shippingFee = freeShipping ? 0 : SHIPPING_FEE;
  const panzerglasPrice = company?.panzerglas_active ? Number(company.panzerglas_price || 0) : 0;
  const cleaningPrice = company?.geraetereinigung_active ? Number(company.geraetereinigung_price || 0) : 0;
  const addonsTotal = (wantPanzerglas ? panzerglasPrice : 0) + (wantCleaning ? cleaningPrice : 0);
  const total = hasService ? discountedBase + shippingFee + addonsTotal : 0;

  const nameOf = (key) => serviceTypes.find((s) => s.key === key)?.name || key;
  const serviceLabel = [
    wantDisplay ? `Display (${displayType === "oled" ? "OLED" : "LCD"})` : null,
    ...selectedKeys.map(nameOf),
  ].filter(Boolean).join(" + ") || "—";

  const showAkkuWarning = selectedKeys.length > 0 && !wantDisplay;

  const toggleService = (key) => setSelectedKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const lookupCustomer = async () => {
    const ident = customerQuery.trim();
    if (!ident) { toast.error("Bitte E-Mail oder Kundennummer eingeben."); return; }
    setCustomerLookupLoading(true);
    try {
      const { data } = await api.post("/customers/lookup", { identifier: ident });
      setMatchedCustomer(data);
      setForm((f) => ({
        ...f,
        first_name: data.first_name || f.first_name,
        last_name: data.last_name || f.last_name,
        email: data.email || f.email,
        phone: data.phone || f.phone,
        street: data.street || f.street,
        zip: data.zip || f.zip,
        city: data.city || f.city,
      }));
      toast.success(data.discount_percent > 0
        ? `Willkommen zurück, ${data.first_name}! Deine Daten wurden übernommen – du erhältst ${data.discount_percent}% Rabatt.`
        : `Willkommen zurück, ${data.first_name}! Deine Daten wurden übernommen.`);
    } catch (err) {
      setMatchedCustomer(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kein Kunde gefunden.");
    } finally { setCustomerLookupLoading(false); }
  };

  const clearCustomer = () => { setMatchedCustomer(null); setCustomerQuery(""); };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error("Bitte einen Rabattcode eingeben."); return; }
    if (coupons.some((c) => c.code === code)) { toast.error("Dieser Code wurde bereits angewendet."); return; }
    setCouponLoading(true);
    try {
      const { data } = await api.post("/discount-codes/validate", { code });
      setCoupons((prev) => [...prev, data]);
      setCouponInput("");
      toast.success(`Rabattcode ${data.code} wurde angewendet.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Dieser Rabattcode ist ungültig.");
    } finally { setCouponLoading(false); }
  };

  const removeCoupon = (code) => setCoupons((prev) => prev.filter((c) => c.code !== code));

  const startCheckout = async (orderId) => {
    const { data } = await api.post("/payments/checkout", { order_id: orderId, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!hasService) { toast.error("Bitte wähle mindestens eine Reparatur."); return; }
    if (addProtection && !deviceSerial.trim()) { toast.error("Bitte gib die Seriennummer / IMEI für das Schutzpaket an."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        ...form, device,
        wants_display: wantDisplay,
        display_type: wantDisplay ? displayType : "oled",
        service_keys: selectedKeys,
        wants_panzerglas: wantPanzerglas,
        wants_cleaning: wantCleaning,
        customer_id: matchedCustomer?.id || undefined,
        discount_codes: coupons.map((c) => c.code),
      });
      if (addProtection) {
        sessionStorage.setItem("gr_pending_protection", JSON.stringify({
          first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
          street: form.street, zip: form.zip, city: form.city, device, device_serial: deviceSerial.trim(),
        }));
      } else {
        sessionStorage.removeItem("gr_pending_protection");
      }
      setForm(empty);
      clearCustomer();
      setCoupons([]);
      setCouponInput("");
      setWantPanzerglas(false);
      setWantCleaning(false);
      try {
        await startCheckout(data.id);
        // on success the browser navigates away to Stripe Checkout
      } catch (payErr) {
        setDone(data);
        toast.error("Bestellung gespeichert, aber die Zahlung konnte nicht gestartet werden. Bitte versuche es erneut.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <section id="beauftragen" data-testid="order" className="relative z-10 bg-[#050505] py-20 lg:py-28 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div data-testid="order-payment-retry" className="glass rounded-3xl p-10 lg:p-14">
            <AlertTriangle size={48} className="text-amber-400 mb-6" />
            <h1 className="font-display text-4xl lg:text-5xl">Bestellung gespeichert – Zahlung ausstehend.</h1>
            <p className="text-[#aaa] mt-4 leading-relaxed">
              Danke, {done.first_name}! Deine {done.service_label} für dein {done.device} wurde gespeichert, die
              Zahlung konnte aber nicht automatisch gestartet werden. Bitte schließe die Zahlung ab, damit wir mit der Reparatur starten können.
            </p>

            <div className="mt-6 rounded-2xl border border-white/15 bg-white/5 p-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-[#888]">Deine Referenznummer</p>
                <p data-testid="order-reference" className="font-mono text-2xl text-white mt-1">{done.reference}</p>
              </div>
              <span className="font-mono text-white text-2xl">{done.total.toFixed(2)} €</span>
            </div>

            <button onClick={() => startCheckout(done.id).catch(() => toast.error("Zahlung konnte nicht gestartet werden. Bitte versuche es gleich noch einmal."))}
              data-testid="order-retry-payment" className="w-full mt-8 rounded-full bg-white text-[#050505] py-4 text-base font-medium hover:bg-[#d1d5db] transition-colors duration-300">
              Jetzt bezahlen
            </button>
            <button onClick={() => setDone(null)} data-testid="order-new" className="mt-4 text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
              Weitere Reparatur beauftragen
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="beauftragen" data-testid="order" className="relative z-10 bg-[#050505] py-20 lg:py-28 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <Link to="/" data-testid="order-back-home" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#777] hover:text-white transition-colors duration-300 mb-6">
            <ArrowLeft size={14} /> Zurück zur Startseite
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">Reparatur beauftragen</p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            Gerät wählen, Preis sehen,<br />einsenden.
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <form onSubmit={submit} data-testid="order-form" className="lg:col-span-7 space-y-6">
            {/* Device */}
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
              <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-3">1 · Dein iPhone</label>
              <select data-testid="order-device" value={device} onChange={(e) => setDevice(e.target.value)} className={inputCls}>
                {pricing.map((p) => <option key={p.id} value={p.model} className="bg-[#0a0a0a]">{p.model}</option>)}
              </select>
            </div>

            {/* Services (multi) */}
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
              <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-4">2 · Reparatur wählen (Mehrfachauswahl möglich)</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button type="button" onClick={() => setWantDisplay((v) => !v)} data-testid="order-toggle-display"
                  className={`text-left rounded-2xl border p-5 transition-colors duration-300 ${wantDisplay ? "border-white bg-white text-[#050505]" : "border-white/10 text-white hover:border-white/40"}`}>
                  <Smartphone size={22} />
                  <p className="font-display text-2xl mt-3">Display-Reparatur</p>
                  <p className={`font-mono text-sm mt-1 ${wantDisplay ? "text-[#050505]/70" : "text-[#999]"}`}>{row ? `ab ${row.lcd_price.toFixed(0)} €` : "—"}</p>
                </button>
                {serviceTypes.map((svc) => {
                  const Icon = iconFor(svc.key);
                  const active = selectedKeys.includes(svc.key);
                  const price = priceOf(svc.key);
                  const original = originalOf(svc.key);
                  return (
                    <button key={svc.key} type="button" onClick={() => toggleService(svc.key)} data-testid={`order-toggle-${svc.key}`}
                      className={`text-left rounded-2xl border p-5 transition-colors duration-300 ${active ? "border-white bg-white text-[#050505]" : "border-white/10 text-white hover:border-white/40"}`}>
                      <Icon size={22} />
                      <p className="font-display text-2xl mt-3">{svc.name}</p>
                      <p className={`font-mono text-sm mt-1 flex items-center gap-2 ${active ? "text-[#050505]/70" : "text-[#999]"}`}>
                        {original > price && price > 0 && <span className="line-through opacity-60">{original.toFixed(0)} €</span>}
                        {row ? (price > 0 ? `${price.toFixed(0)} €` : "Preis auf Anfrage") : "—"}
                      </p>
                      {svc.key === "akku" && (
                        <span data-testid="badge-garantie-akku" className={`inline-block mt-2.5 text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5 border ${active ? "text-amber-800 bg-amber-500/10 border-amber-600/30" : "text-amber-300 bg-amber-500/10 border-amber-500/30"}`}>
                          Verschleißteil · keine Garantie
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* OLED / LCD choice — prominent with pros & cons */}
              {wantDisplay && (
                <div className="mt-6" data-testid="display-type-choice">
                  <p className="text-xs font-mono uppercase tracking-widest text-[#888] mb-3">Display-Qualität wählen</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* OLED */}
                    <button type="button" disabled={!oledAvailable} onClick={() => setDisplayType("oled")} data-testid="display-oled"
                      className={`text-left rounded-2xl border p-5 flex items-start gap-4 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed ${displayType === "oled" && oledAvailable ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/40"}`}>
                      <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${displayType === "oled" && oledAvailable ? "border-white" : "border-white/25"}`}>
                        {displayType === "oled" && oledAvailable && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-xl text-white">Original Display</p>
                            <span className="inline-block mt-1.5 mr-1.5 text-[10px] font-mono uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">Empfohlen</span>
                            <span data-testid="badge-garantie-oled" className="inline-block mt-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">6 Monate Garantie</span>
                          </div>
                          <div className="text-right shrink-0">
                            {oledOriginal > displayPriceOled && displayPriceOled > 0 && (
                              <p className="text-xs text-[#777] line-through">{oledOriginal.toFixed(2)} €</p>
                            )}
                            <p className="font-mono text-white text-xl whitespace-nowrap">{oledAvailable && row ? `${displayPriceOled.toFixed(2)} €` : "—"}</p>
                          </div>
                        </div>
                        <p className="text-sm text-[#999] mt-2 leading-relaxed">
                          Originales OLED-Display in Werksqualität – volle Kompatibilität mit True Tone, Face ID und maximaler Farbgenauigkeit.
                        </p>
                      </div>
                    </button>

                    {/* LCD */}
                    <button type="button" onClick={() => setDisplayType("lcd")} data-testid="display-lcd"
                      className={`text-left rounded-2xl border p-5 flex items-start gap-4 transition-all duration-300 ${displayType === "lcd" ? "border-white bg-white/[0.06]" : "border-white/10 hover:border-white/40"}`}>
                      <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${displayType === "lcd" ? "border-white" : "border-white/25"}`}>
                        {displayType === "lcd" && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-xl text-white">LCD</p>
                            <span data-testid="badge-garantie-lcd" className="inline-block mt-1.5 text-[10px] font-mono uppercase tracking-widest text-[#999] bg-white/5 border border-white/15 rounded-full px-2 py-0.5">Keine Garantie</span>
                          </div>
                          <div className="text-right shrink-0">
                            {lcdOriginal > displayPriceLcd && displayPriceLcd > 0 && (
                              <p className="text-xs text-[#777] line-through">{lcdOriginal.toFixed(2)} €</p>
                            )}
                            <p className="font-mono text-white text-xl whitespace-nowrap">{row ? `${displayPriceLcd.toFixed(2)} €` : "—"}</p>
                          </div>
                        </div>
                        <p className="text-sm text-[#999] mt-2 leading-relaxed">
                          Günstige, voll funktionsfähige Alternative – ideal, wenn dir der Preis wichtiger ist als Original-Farbgenauigkeit.
                        </p>
                      </div>
                    </button>
                  </div>
                  {!oledAvailable && (
                    <p className="text-xs text-[#888] mt-3" data-testid="oled-unavailable">Für dieses Modell ist ab Werk ein LCD verbaut – OLED ist daher nicht verfügbar.</p>
                  )}
                </div>
              )}

              {/* Red warning box */}
              {showAkkuWarning && (
                <div data-testid="akku-warning" className="mt-5 rounded-xl border border-red-500/40 bg-red-500/10 p-4 flex gap-3">
                  <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-200 leading-relaxed">
                    <strong>Wichtiger Hinweis:</strong> Falls dein Display bereits Vorschäden hat (Risse, Sprünge),
                    empfehlen wir beim Öffnen des Geräts dringend, das Display gleich mit zu tauschen – ein
                    vorgeschädigtes Display geht beim Öffnen mit hoher Wahrscheinlichkeit kaputt.
                  </p>
                </div>
              )}
            </div>

            {/* Optional extras */}
            {(company?.panzerglas_active || company?.geraetereinigung_active) && (
              <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
                <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-4">3 · Zusätzliche Extras (optional)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {company?.panzerglas_active && (
                    <button type="button" onClick={() => setWantPanzerglas((v) => !v)} data-testid="order-toggle-panzerglas"
                      className={`text-left rounded-2xl border p-5 transition-colors duration-300 ${wantPanzerglas ? "border-white bg-white text-[#050505]" : "border-white/10 text-white hover:border-white/40"}`}>
                      <Layers size={22} />
                      <p className="font-display text-2xl mt-3">+ Panzerglas</p>
                      <p className={`text-sm mt-1 leading-relaxed ${wantPanzerglas ? "text-[#050505]/70" : "text-[#999]"}`}>Zusätzlicher Displayschutz für dein Gerät.</p>
                      <p className={`font-mono text-sm mt-2 ${wantPanzerglas ? "text-[#050505]/70" : "text-[#999]"}`}>{panzerglasPrice.toFixed(2)} €</p>
                    </button>
                  )}
                  {company?.geraetereinigung_active && (
                    <button type="button" onClick={() => setWantCleaning((v) => !v)} data-testid="order-toggle-cleaning"
                      className={`text-left rounded-2xl border p-5 transition-colors duration-300 ${wantCleaning ? "border-white bg-white text-[#050505]" : "border-white/10 text-white hover:border-white/40"}`}>
                      <Droplets size={22} />
                      <p className="font-display text-2xl mt-3">+ Gerätereinigung</p>
                      <p className={`text-sm mt-1 leading-relaxed ${wantCleaning ? "text-[#050505]/70" : "text-[#999]"}`}>Lautsprecher, Hörmuschel, Ladeanschluss.</p>
                      <p className={`font-mono text-sm mt-2 ${wantCleaning ? "text-[#050505]/70" : "text-[#999]"}`}>{cleaningPrice.toFixed(2)} €</p>
                    </button>
                  )}
                </div>
              </div>
            )}
            {/* Already a customer? */}
            <div data-testid="customer-lookup-box" className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
              <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-3">4 · Bereits Kunde?</label>
              {matchedCustomer ? (
                <div data-testid="customer-lookup-success" className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                  <div className="flex items-center gap-3">
                    <UserCheck size={20} className="text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-sm text-white">Willkommen zurück, {matchedCustomer.first_name}! Deine Daten wurden übernommen.</p>
                      {matchedCustomer.discount_percent > 0 && (
                        <p className="text-xs text-emerald-300 mt-1 inline-flex items-center gap-1.5">
                          <BadgePercent size={13} /> {matchedCustomer.discount_percent}% Stammkunden-Rabatt wird automatisch angewendet.
                        </p>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={clearCustomer} data-testid="customer-lookup-clear" className="shrink-0 text-xs text-[#aaa] hover:text-white underline underline-offset-4 transition-colors">
                    Entfernen
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="customer-lookup-input"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="E-Mail oder Kundennummer (optional)"
                    className={`${inputCls} flex-1`}
                  />
                  <button
                    type="button"
                    onClick={lookupCustomer}
                    disabled={customerLookupLoading}
                    data-testid="customer-lookup-button"
                    className="shrink-0 rounded-xl border border-white/20 px-6 py-3.5 text-sm text-white hover:bg-white/5 transition-colors disabled:opacity-50"
                  >
                    {customerLookupLoading ? "Suche…" : "Daten laden"}
                  </button>
                </div>
              )}
              <p data-testid="customer-lookup-hint" className="text-xs text-red-400 mt-3 leading-relaxed">
                Schon bei uns Kunde? Gib deine E-Mail oder Kundennummer ein, damit wir deine Daten automatisch ausfüllen – inklusive eventueller Rabatte oder besserer Konditionen.
              </p>
            </div>

            {/* Discount code */}
            <div data-testid="coupon-box" className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7">
              <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-3">5 · Rabattcode (optional, mehrere möglich)</label>
              {coupons.length > 0 && (
                <div className="flex flex-col gap-2 mb-3">
                  {coupons.map((c) => (
                    <div key={c.code} data-testid={`coupon-applied-${c.code}`} className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                      <div className="flex items-center gap-3">
                        <BadgePercent size={20} className="text-emerald-400 shrink-0" />
                        <p className="text-sm text-white">Code <span className="font-mono">{c.code}</span> wurde angewendet.</p>
                      </div>
                      <button type="button" onClick={() => removeCoupon(c.code)} data-testid={`coupon-remove-${c.code}`} className="shrink-0 text-xs text-[#aaa] hover:text-white underline underline-offset-4 transition-colors">
                        Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="coupon-input"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  placeholder="Weiteren Rabattcode eingeben"
                  className={`${inputCls} flex-1`}
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={couponLoading}
                  data-testid="coupon-apply-btn"
                  className="shrink-0 rounded-xl border border-white/20 px-6 py-3.5 text-sm text-white hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  {couponLoading ? "Prüfe…" : "Anwenden"}
                </button>
              </div>
            </div>

            {/* Contact + shipping */}
            <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 space-y-5">
              <label className="block text-xs font-mono uppercase tracking-widest text-[#888]">6 · Deine Daten</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="of-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="Vorname" className={inputCls} />
                <input data-testid="of-last-name" required value={form.last_name} onChange={set("last_name")} placeholder="Nachname" className={inputCls} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="of-email" required type="email" value={form.email} onChange={set("email")} placeholder="E-Mail" className={inputCls} />
                <input data-testid="of-phone" required value={form.phone} onChange={set("phone")} placeholder="Telefon" className={inputCls} />
              </div>
              <input data-testid="of-street" required value={form.street} onChange={set("street")} placeholder="Straße & Hausnummer" className={inputCls} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input data-testid="of-zip" required value={form.zip} onChange={set("zip")} placeholder="PLZ" className={inputCls} />
                <input data-testid="of-city" required value={form.city} onChange={set("city")} placeholder="Ort" className={`${inputCls} sm:col-span-2`} />
              </div>
              <textarea data-testid="of-message" rows={3} value={form.message} onChange={set("message")} placeholder="Fehlerbeschreibung (optional)" className={`${inputCls} resize-none`} />
              <div>
                <input data-testid="of-device-code" value={form.device_code} onChange={set("device_code")} placeholder="Gerätecode (Entsperrcode, optional)" className={inputCls} />
                <p data-testid="of-device-code-hint" className="text-xs text-red-400 mt-2 leading-relaxed">
                  Wird benötigt, um die neuen Ersatzteile auf deinem iPhone zu programmieren und zu aktivieren. Nach der Reparatur wird der Code gelöscht. Der Code ist optional – ohne Angabe können die neuen Teile jedoch nicht programmiert werden, wodurch ggf. entsprechende Meldungen in den iPhone-Einstellungen angezeigt werden können.
                </p>
              </div>
            </div>

            {/* Protection plan add-on */}
            {(() => {
              const row = pricing.find((p) => p.model === device);
              const price = protectionSettings && row?.protection_tier ? protectionSettings.tiers[row.protection_tier] : null;
              if (price == null) return null;
              return (
                <div className="rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-[#0d1410] to-[#0a0a0a] p-7">
                  <button type="button" onClick={() => setAddProtection((v) => !v)} data-testid="order-add-protection-toggle"
                    className="w-full text-left flex items-start gap-4">
                    <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${addProtection ? "bg-emerald-400 border-emerald-400" : "border-white/25"}`}>
                      {addProtection && <ShieldCheck size={13} className="text-[#050505]" />}
                    </span>
                    <span>
                      <span className="block text-white font-medium">
                        Schutzpaket für dieses Gerät dazu buchen – {price.toFixed(2)} € / Monat
                      </span>
                      <span className="block text-xs text-[#999] mt-1 leading-relaxed">
                        Gilt ab der nächsten Reparatur (nicht für die jetzige): {protectionSettings?.repairs_per_year || 1} weitere Reparatur(en) pro Jahr inklusive,
                        {" "}{protectionSettings ? Math.round(protectionSettings.min_term_months / 12) : 2} Jahre Mindestlaufzeit. Du schließt es separat nach der Zahlung ab.
                      </span>
                    </span>
                  </button>
                  {addProtection && (
                    <input data-testid="order-protection-serial" value={deviceSerial} onChange={(e) => setDeviceSerial(e.target.value)}
                      placeholder="Seriennummer / IMEI für das Schutzpaket" className={`${inputCls} mt-4`} />
                  )}
                </div>
              );
            })()}
          </form>

          {/* Summary */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-3xl border border-white/10 bg-[#0a0a0a] p-8">
              <h3 className="font-display text-3xl mb-6">Deine Bestellung</h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between"><span className="text-[#888]">Modell</span><span className="text-white">{device || "—"}</span></div>
                <div className="flex justify-between"><span className="text-[#888]">Reparatur</span><span className="text-white text-right">{serviceLabel}</span></div>
                {wantDisplay && <div className="flex justify-between border-t border-white/10 pt-4"><span className="text-[#888]">Display ({displayType.toUpperCase()})</span><span className="text-white font-mono">{displayPrice.toFixed(2)} €</span></div>}
                {selectedKeys.map((k) => (
                  <div key={k} className="flex justify-between"><span className="text-[#888]">{nameOf(k)}</span><span className="text-white font-mono">{priceOf(k).toFixed(2)} €</span></div>
                ))}
                {discountPercent > 0 && !couponWins && (
                  <div data-testid="order-discount-row" className="flex justify-between text-emerald-300">
                    <span className="flex items-center gap-1.5"><BadgePercent size={14} /> Stammkunden-Rabatt ({discountPercent}%)</span>
                    <span className="font-mono">−{discountAmount.toFixed(2)} €</span>
                  </div>
                )}
                {couponWins && (
                  <div data-testid="order-coupon-row" className="flex justify-between text-emerald-300">
                    <span className="flex items-center gap-1.5"><BadgePercent size={14} /> Rabattcode(s) {coupons.filter((c) => c.type !== "free_shipping").map((c) => c.code).join(" + ")}</span>
                    <span className="font-mono">−{discountAmount.toFixed(2)} €</span>
                  </div>
                )}
                {freeShipping && (
                  <div data-testid="order-free-shipping-row" className="flex justify-between text-emerald-300">
                    <span className="flex items-center gap-1.5"><Truck size={14} /> Rabattcode {coupons.find((c) => c.type === "free_shipping")?.code}</span>
                    <span className="font-mono">Kostenloser Versand</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-white/10 pt-4"><span className="text-[#888] flex items-center gap-2"><Truck size={15} /> Versand (Hin & zurück)</span><span className="text-white font-mono">{hasService ? shippingFee.toFixed(2) : "0.00"} €</span></div>
                {wantPanzerglas && (
                  <div data-testid="order-addon-panzerglas-row" className="flex justify-between"><span className="text-[#888]">+ Panzerglas</span><span className="text-white font-mono">{panzerglasPrice.toFixed(2)} €</span></div>
                )}
                {wantCleaning && (
                  <div data-testid="order-addon-cleaning-row" className="flex justify-between"><span className="text-[#888]">+ Gerätereinigung</span><span className="text-white font-mono">{cleaningPrice.toFixed(2)} €</span></div>
                )}
              </div>
              <div className="flex justify-between items-baseline mt-6 pt-6 border-t border-white/10">
                <span className="text-white">Gesamt</span>
                <span data-testid="order-price-total" className="font-display text-4xl text-white">{total.toFixed(2)} €</span>
              </div>
              {taxNote && <p data-testid="order-tax-note" className="text-xs text-[#777] mt-2">{taxNote}</p>}

              <button type="button" onClick={submit} disabled={loading} data-testid="submit-order"
                className="w-full mt-8 rounded-full bg-white text-[#050505] py-4 text-base font-medium hover:bg-[#d1d5db] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                <Package size={18} /> {loading ? "Wird vorbereitet…" : "Jetzt bezahlen & beauftragen"}
              </button>
              <p className="text-xs text-[#666] mt-4 leading-relaxed flex items-start gap-2">
                <Truck size={14} className="mt-0.5 shrink-0" />
                Du wirst zur sicheren Zahlung über Stripe weitergeleitet. Nach der Zahlung sendest du dein Gerät ein – wir reparieren es und schicken es versichert zurück.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
